import React, { useEffect, useState } from "react";
import { getUsers } from "../services/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaSearch, FaFileExcel, FaEye, FaCalendarAlt } from "react-icons/fa";

export default function Users({ onView }) {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10; // Increased page size for better usability

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, fromDate, toDate, users]);

  const load = async () => {
    const data = await getUsers();
    setUsers(data);
  };

  // 📅 Date formatter → dd-MMM-yyyy
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // 🔍 FILTER LOGIC
  const applyFilters = () => {
    let data = [...users];

    // 🔍 Search (Matches "Name" in your data)
    if (search) {
      data = data.filter((u) =>
        u.data?.Name?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // 📅 From Date
    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);

      data = data.filter((u) => {
        const d = new Date(u.createdDate);
        d.setHours(0, 0, 0, 0);
        return d >= from;
      });
    }

    // 📅 To Date
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);

      data = data.filter((u) => {
        const d = new Date(u.createdDate);
        return d <= to;
      });
    }

    setFiltered(data);
    setPage(1);
  };

  // 📊 PAGINATION
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // 📥 EXPORT (Matches your specific data keys)
  const exportToExcel = () => {
    const exportData = filtered.map((u) => ({
      "Patient Name": u.data?.Name,
      "Age": u.data?.Age,
      "Sex": u.data?.Sex,
      "Score": u.data?.Raw_Score,
      "Risk Level": u.data?.Risk_Level,
      "Probability (%)": u.data?.Fatty_Liver_Probability,
      "Date": formatDate(u.createdDate),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Patients");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(file, `Patient_Records_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // 🎨 Risk badge (Logic updated for "Risk_Level" strings)
  const getBadge = (riskString) => {
    const risk = riskString?.toLowerCase() || "";
    if (risk.includes("high"))
      return <span className="badge bg-danger px-3">High Risk</span>;
    if (risk.includes("moderate"))
      return <span className="badge bg-warning text-dark px-3">Moderate</span>;
    return <span className="badge bg-success px-3">Mild Risk</span>;
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark mb-0">Patient Records</h2>
        <button className="btn btn-success d-flex align-items-center gap-2 shadow-sm" onClick={exportToExcel}>
          <FaFileExcel /> Export Excel
        </button>
      </div>

      {/* 🔥 FILTER BAR */}
      <div className="glass-card p-3 mb-4 shadow-sm border-0">
        <div className="row g-3 align-items-end">
          <div className="col-md-5">
            <label className="small fw-bold text-muted mb-1"><FaSearch className="me-1"/> Search</label>
            <input
              className="form-control border-light"
              placeholder="Search by patient name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="small fw-bold text-muted mb-1"><FaCalendarAlt className="me-1"/> From</label>
            <input
              type="date"
              className="form-control border-light"
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="small fw-bold text-muted mb-1"><FaCalendarAlt className="me-1"/> To</label>
            <input
              type="date"
              className="form-control border-light"
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          
          <div className="col-md-1">
             <button className="btn btn-outline-secondary w-100" onClick={() => {setSearch(""); setFromDate(""); setToDate("");}}>
                Reset
             </button>
          </div>
        </div>
      </div>

      {/* 📋 TABLE */}
      <div className="glass-card shadow-sm border-0 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr className="text-muted small">
                <th className="ps-4">NAME</th>
                <th>SCORE</th>
                <th>RISK LEVEL</th>
                <th>PROBABILITY</th>
                <th>DATE</th>
                <th className="text-end pe-4">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((u) => {
                const d = u.data; // Matches lower-case 'data' from your API result
                const isHighRisk = d?.Risk_Level?.toLowerCase().includes("high");

                return (
                  <tr key={u.id} className={isHighRisk ? "table-danger-light" : ""}>
                    <td className="ps-4">
                      <div className="fw-bold text-dark">{d?.Name}</div>
                      <small className="text-muted">{d?.Sex} • Age {d?.Age}</small>
                    </td>
                    <td><span className="fw-semibold">{d?.Raw_Score}</span></td>
                    <td>{getBadge(d?.Risk_Level)}</td>
                    <td>
                        <div className="d-flex align-items-center gap-2">
                            <div className="progress flex-grow-1" style={{height: "6px", width: "60px"}}>
                                <div 
                                    className={`progress-bar ${isHighRisk ? 'bg-danger' : 'bg-primary'}`} 
                                    style={{width: `${d?.Fatty_Liver_Probability}%`}}
                                ></div>
                            </div>
                            <small>{d?.Fatty_Liver_Probability}%</small>
                        </div>
                    </td>
                    <td className="text-muted">{formatDate(u.createdDate)}</td>
                    <td className="text-end pe-4">
                      <button
                        className="btn btn-sm btn-light border"
                        onClick={() => onView(u.id)}
                      >
                        <FaEye className="me-1"/> Details
                      </button>
                    </td>
                  </tr>
                );
              })}

              {paginated.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No records matching your search criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 📊 PAGINATION */}
        <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
          <button
            className="btn btn-sm btn-white border shadow-sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>

          <span className="small fw-bold">
            Page {page} of {totalPages || 1} <span className="text-muted fw-normal">({filtered.length} total)</span>
          </span>

          <button
            className="btn btn-sm btn-white border shadow-sm"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}